import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users as UsersIcon, Search, Car, Store, UserRound, Wifi, Loader2, ShieldCheck, ArrowUpDown, Check, Bike, CarFront, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import ProfileBlock from "../../components/admin/ProfileBlock";
import { PageHeader, StatusBadge, EmptyState, LoadingBlock, StatCard } from "../../components/app/ui";
import { adminApi, errorMessage, formatDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { isCommercialCategory, isPrivateCarCategory, VEHICLE_CATEGORIES, categoryOf } from "../../lib/driver";

const INTERCITY_CATS = ["commercial_car", "commercial_jumbo"];

// Where to send the admin to review a partner's KYC (deep-links the review sheet).
function kycReviewPath(u) {
  if (u.role === "restaurant") return `/app/admin/verify/restaurants?user=${u.user_id}`;
  const cat = u.vehicle_category;
  const seg = isPrivateCarCategory(cat) ? "private-cars" : isCommercialCategory(cat) ? "commercial-drivers" : "delivery-partners";
  return `/app/admin/verify/${seg}?user=${u.user_id}`;
}
const needsKycReview = (u) => u.is_partner && ["pending", "unverified", "rejected"].includes(u.verification_status);

const ROLE_TABS = [
  { value: "all", label: "All" },
  { value: "rider", label: "Customers" },
  { value: "driver", label: "Drivers" },
  { value: "restaurant", label: "Restaurants" },
  { value: "admin", label: "Admins" },
];

// Driver sub-categories (match backend USER_TYPE_LABELS) for the admin filter.
const DRIVER_TYPES = ["Taxi Driver", "Private Taxi Driver", "Auto Driver", "Bike Driver", "Jumbo Taxi Driver", "Delivery only"];
const SORT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "type", label: "Type (A–Z)" },
  { value: "status", label: "Status (online first)" },
  { value: "kyc", label: "KYC (needs review first)" },
];
const KYC_ORDER = { pending: 0, unverified: 1, rejected: 2, verified: 3 };

function OnlineDot({ online }) {
  if (online === null || online === undefined) return <span className="text-ink-muted">—</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${online ? "bg-green-100 text-green-700" : "bg-surface-200 text-ink-soft"}`} data-testid="user-online-status">
      <span className={`h-2 w-2 rounded-full ${online ? "bg-green-500" : "bg-ink-muted"}`} />
      {online ? "Online" : "Offline"}
    </span>
  );
}

export default function Users() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [data, setData] = useState(null);
  const [editUser, setEditUser] = useState(null); // { user_id, name, role }
  const [editBusy, setEditBusy] = useState(false);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [onlineKind, setOnlineKind] = useState(null); // taxi | delivery | private_car | null (driver sub-kind via online cards)
  const [driverType, setDriverType] = useState("all");
  const [sortBy, setSortBy] = useState("default");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dtype, setDtype] = useState(null);
  const [dtypeCat, setDtypeCat] = useState("");
  const [dtypeScope, setDtypeScope] = useState("local");
  const [dtypeBusy, setDtypeBusy] = useState(false);

  const load = (silent = false) => {
    if (!silent) setData(null);
    adminApi.users().then(setData).catch((e) => { if (!silent) { toast.error(errorMessage(e)); setData({ users: [], counts: {} }); } });
  };
  useEffect(() => { load(); }, []);
  // Live auto-refresh: silently refresh counts + list every 8s so the admin sees
  // partners come online / go offline in near real-time (no spinner, keeps filters).
  useEffect(() => {
    const id = setInterval(() => load(true), 8000);
    return () => clearInterval(id);
  }, []);

  // Jump to a specific online partner group from the "Partners online" cards.
  const showOnline = (r, kind) => { setRole(r); setOnlineOnly(true); setOnlineKind(kind); setDriverType("all"); };

  const openDetail = async (userId) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try { const d = await adminApi.userDetail(userId); setDetail(d.user); }
    catch (e) { toast.error(errorMessage(e)); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errorMessage(e)); } };
  const suspend = (u) => { if (window.confirm(`Suspend ${u.name}? They'll be signed out and blocked from logging in until reactivated.`)) act(() => adminApi.adminSuspend(u.user_id), "Account suspended"); };
  const unsuspend = (u) => act(() => adminApi.adminUnsuspend(u.user_id), "Account reactivated");
  const remove = (u) => { if (window.confirm(`Permanently delete ${u.name}? This removes their account and cannot be undone.`)) act(() => adminApi.adminDeleteUser(u.user_id), "Account deleted"); };

  const saveEdit = async () => {
    if (!editUser) return;
    setEditBusy(true);
    try {
      await adminApi.updateUser(editUser.user_id, { name: editUser.name, role: editUser.role });
      toast.success("User updated");
      setEditUser(null);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setEditBusy(false); }
  };

  const openDtype = async (u) => {
    setDtype({ ...u, loading: true });
    try {
      const d = await adminApi.verification(u.user_id);
      const user = d?.user || u;
      setDtype(user);
      setDtypeCat(categoryOf(user) || "private_scooter");
      setDtypeScope(user.taxi_scope || "local");
    } catch (e) { toast.error(errorMessage(e)); setDtype(u); setDtypeCat(categoryOf(u) || "private_scooter"); setDtypeScope(u.taxi_scope || "local"); }
  };
  const saveDtype = async () => {
    setDtypeBusy(true);
    try {
      await adminApi.setDriverType(dtype.user_id, dtypeCat, INTERCITY_CATS.includes(dtypeCat) ? dtypeScope : undefined);
      toast.success(`Driver type set to ${VEHICLE_CATEGORIES.find((c) => c.value === dtypeCat)?.label || dtypeCat}`);
      setDtype(null); load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setDtypeBusy(false); }
  };
  const dtypeChanged = dtype && (dtypeCat !== (categoryOf(dtype) || "") || (INTERCITY_CATS.includes(dtypeCat) && dtypeScope !== (dtype.taxi_scope || "local")));

  const users = data?.users || null;
  const counts = data?.counts || {};
  const driverTypeCounts = useMemo(() => {
    const m = {};
    (users || []).filter((u) => u.role === "driver").forEach((u) => { m[u.user_type_label] = (m[u.user_type_label] || 0) + 1; });
    return m;
  }, [users]);
  const filtered = useMemo(() => {
    let list = (users || []).filter((u) =>
      (role === "all" || u.role === role)
      && (!onlineOnly || u.online === true)
      && (!onlineKind || u.driver_kind === onlineKind)
      && (role !== "driver" || driverType === "all" || u.user_type_label === driverType)
      && (!q.trim() || `${u.name} ${u.email} ${u.phone || ""} ${u.user_type_label}`.toLowerCase().includes(q.trim().toLowerCase()))
    );
    if (sortBy === "type") list = [...list].sort((a, b) => (a.user_type_label || "").localeCompare(b.user_type_label || ""));
    else if (sortBy === "status") list = [...list].sort((a, b) => (b.online === true ? 1 : 0) - (a.online === true ? 1 : 0));
    else if (sortBy === "kyc") list = [...list].sort((a, b) => (KYC_ORDER[a.verification_status] ?? 9) - (KYC_ORDER[b.verification_status] ?? 9));
    return list;
  }, [users, role, q, onlineOnly, onlineKind, driverType, sortBy]);

  return (
    <>
      <PageHeader title="Users" description="Every account on the platform — customers, drivers, restaurants and admins — with their detailed type and live status." />
      <div className="mb-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="All accounts" value={counts.all ?? "…"} icon={UsersIcon} />
        <StatCard label="Customers" value={counts.rider ?? "…"} icon={UserRound} tone="graphite" />
        <StatCard label="Drivers" value={counts.driver ?? "…"} icon={Car} tone="ink" />
        <StatCard label="Restaurants" value={counts.restaurant ?? "…"} icon={Store} tone="graphite" />
      </div>
      <div className="mb-6">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted" data-testid="partners-online-heading"><Wifi className="h-3.5 w-3.5 text-brand" /> Partners online · {counts.online ?? "…"} <span className="ml-1 normal-case text-[11px] font-normal text-ink-muted">· tap a card to filter the list</span></p>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { kind: "taxi", role: "driver", label: "Taxi drivers online", value: counts.online_taxi, icon: Car, testid: "online-card-taxi" },
            { kind: "restaurant", role: "restaurant", label: "Restaurants online", value: counts.online_restaurant, icon: Store, testid: "online-card-restaurant" },
            { kind: "delivery", role: "driver", label: "Delivery partners online", value: counts.online_delivery, icon: Bike, testid: "online-card-delivery" },
            { kind: "private_car", role: "driver", label: "Private car online", value: counts.online_private_car, icon: CarFront, testid: "online-card-private-car" },
          ].map((c) => {
            const active = onlineOnly && role === c.role && (c.role === "restaurant" ? !onlineKind : onlineKind === c.kind);
            return (
              <button
                key={c.kind}
                type="button"
                onClick={() => showOnline(c.role, c.role === "restaurant" ? null : c.kind)}
                className={`rounded-2xl text-left transition-shadow focus:outline-none ${active ? "ring-2 ring-brand ring-offset-2" : "hover:ring-2 hover:ring-brand/40"}`}
                data-testid={c.testid}
              >
                <StatCard label={c.label} value={c.value ?? "…"} icon={c.icon} tone="brand" />
              </button>
            );
          })}
        </div>
      </div>
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, phone or type" className="h-11 bg-white pl-9" data-testid="users-search" /></div>
        <div className="flex flex-wrap gap-2">{ROLE_TABS.map((r) => <button key={r.value} onClick={() => { setRole(r.value); setOnlineKind(null); }} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${role === r.value ? "bg-ink text-surface" : "bg-white text-ink-soft ring-1 ring-surface-300"}`} data-testid={`users-role-${r.value}`}>{r.label}</button>)}
          <button onClick={() => { setOnlineOnly((v) => !v); setOnlineKind(null); }} className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${onlineOnly ? "bg-green-600 text-white" : "bg-white text-ink-soft ring-1 ring-surface-300"}`} data-testid="users-online-toggle"><Wifi className="h-4 w-4" /> Online only</button>
          <div className="flex items-center gap-1.5" data-testid="users-sort">
            <ArrowUpDown className="h-4 w-4 text-ink-muted" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-10 w-[200px] rounded-full bg-white" data-testid="users-sort-trigger"><SelectValue placeholder="Sort by" /></SelectTrigger>
              <SelectContent>{SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`users-sort-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {role === "driver" && (
        <div className="mb-5 flex flex-wrap gap-2" data-testid="driver-type-filters">
          <button onClick={() => setDriverType("all")} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${driverType === "all" ? "bg-brand text-white" : "bg-white text-ink-soft ring-1 ring-surface-300"}`} data-testid="driver-type-all">All drivers ({counts.driver ?? 0})</button>
          {DRIVER_TYPES.map((t) => (
            <button key={t} onClick={() => setDriverType(t)} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${driverType === t ? "bg-brand text-white" : "bg-white text-ink-soft ring-1 ring-surface-300"}`} data-testid={`driver-type-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t} ({driverTypeCounts[t] || 0})</button>
          ))}
        </div>
      )}
      {users === null && <LoadingBlock rows={5} />}
      {users && filtered.length === 0 && <EmptyState icon={UsersIcon} title="No users match" />}
      {filtered.length > 0 && (
        <div className="kt-card overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-surface-100 text-left text-xs uppercase tracking-wider text-ink-muted"><tr><th className="px-5 py-3 font-semibold">User</th><th className="px-5 py-3 font-semibold">Type</th><th className="px-5 py-3 font-semibold">Status</th><th className="hidden px-5 py-3 font-semibold lg:table-cell">Joined</th><th className="px-5 py-3 font-semibold">KYC</th><th className="px-5 py-3 text-right font-semibold">Actions</th></tr></thead>
            <tbody className="divide-y divide-surface-200">
              {filtered.map((u) => (
                <tr key={u.user_id} className="hover:bg-surface-50" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-5 py-3.5"><button onClick={() => openDetail(u.user_id)} className="text-left font-medium text-ink hover:text-brand hover:underline" data-testid={`user-view-${u.user_id}`}>{u.name}</button>{u.suspended && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">Suspended</span>}{u.upheld_reports >= 2 && <span className="ml-2 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`user-repeat-offender-${u.user_id}`}>⚠ {u.upheld_reports} reports</span>}<p className="text-xs text-ink-soft">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p>{u.role === "restaurant" && u.restaurant_name ? <p className="text-xs font-medium text-ink" data-testid={`user-restaurant-${u.user_id}`}>🍽 {u.restaurant_name}</p> : null}{u.role === "driver" && (u.vehicle_make || u.vehicle_model || u.vehicle_plate) ? <p className="text-xs font-medium text-ink" data-testid={`user-vehicle-${u.user_id}`}>🚗 {[u.vehicle_make, u.vehicle_model].filter(Boolean).join(" ")}{u.vehicle_plate ? ` · ${u.vehicle_plate}` : ""}</p> : null}{u.driver_kind === "private_car" ? <p className="text-xs font-semibold text-brand" data-testid={`user-daily-rate-${u.user_id}`}>💰 {u.daily_rate ? `₹${u.daily_rate}/day` : "Daily rate: not set"}</p> : null}</td>
                  <td className="px-5 py-3.5"><span className="inline-flex rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand" data-testid="user-type-label">{u.user_type_label}</span></td>
                  <td className="px-5 py-3.5">{u.is_partner ? <OnlineDot online={u.online} /> : <span className="text-ink-muted">—</span>}</td>
                  <td className="hidden px-5 py-3.5 text-ink-soft lg:table-cell">{formatDate(u.created_at, { timeStyle: undefined })}</td>
                  <td className="px-5 py-3.5">{u.is_partner ? <StatusBadge status={u.verification_status} /> : <span className="text-ink-muted">—</span>}</td>
                  <td className="px-5 py-3.5 text-right">
                    {["admin", "manager", "agent"].includes(u.role) ? <span className="text-ink-muted">—</span> : (
                      <div className="inline-flex gap-1.5">
                        {needsKycReview(u) && (
                          <button onClick={() => navigate(kycReviewPath(u))} className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand hover:bg-brand-100" data-testid={`user-review-kyc-${u.user_id}`}><ShieldCheck className="h-3.5 w-3.5" /> Review KYC</button>
                        )}
                        {u.role === "driver" && (
                          <button onClick={() => openDtype(u)} className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-200" data-testid={`user-change-type-${u.user_id}`}><Car className="h-3.5 w-3.5" /> Change type</button>
                        )}
                        <button onClick={() => setEditUser({ user_id: u.user_id, name: u.name || "", role: u.role })} className="inline-flex items-center gap-1 rounded-full bg-surface-200 px-2.5 py-1 text-xs font-semibold text-ink-soft hover:bg-surface-300" data-testid={`user-edit-${u.user_id}`}><Pencil className="h-3.5 w-3.5" /> Edit</button>
                        {u.suspended ? (
                          <button onClick={() => unsuspend(u)} className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700 hover:bg-green-200" data-testid={`user-unsuspend-${u.user_id}`}>Reactivate</button>
                        ) : (
                          <button onClick={() => suspend(u)} className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-200" data-testid={`user-suspend-${u.user_id}`}>Suspend</button>
                        )}
                        {isAdmin && <button onClick={() => remove(u)} className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-200" data-testid={`user-delete-${u.user_id}`}>Delete</button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="user-detail-dialog">
          <DialogHeader><DialogTitle>User profile</DialogTitle><DialogDescription>Full account details and moderation actions.</DialogDescription></DialogHeader>
          {detailLoading || detail?.loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : detail ? (
            <div className="space-y-4">
              <ProfileBlock profile={detail} />
              {!["admin", "manager", "agent"].includes(detail.role) && (
                <div className="flex flex-wrap gap-2">
                  {(detail.role === "driver" || detail.role === "restaurant") && ["pending", "unverified", "rejected"].includes(detail.verification_status) && (
                    <Button size="sm" className="rounded-full bg-brand text-white hover:bg-brand-600" onClick={() => { navigate(kycReviewPath(detail)); setDetail(null); }} data-testid="detail-review-kyc"><ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Review KYC</Button>
                  )}
                  {detail.role === "driver" && (
                    <Button size="sm" className="rounded-full bg-indigo-600 text-white hover:bg-indigo-700" onClick={() => { const d = detail; setDetail(null); openDtype(d); }} data-testid="detail-change-type"><Car className="mr-1.5 h-3.5 w-3.5" /> Change type</Button>
                  )}
                  {detail.suspended ? (
                    <Button size="sm" className="rounded-full bg-green-600 text-white hover:bg-green-700" onClick={() => act(() => adminApi.adminUnsuspend(detail.user_id), "Account reactivated").then(() => openDetail(detail.user_id))} data-testid="detail-unsuspend">Reactivate</Button>
                  ) : (
                    <Button size="sm" className="rounded-full bg-amber-500 text-white hover:bg-amber-600" onClick={() => { if (window.confirm(`Suspend ${detail.name}?`)) act(() => adminApi.adminSuspend(detail.user_id), "Account suspended").then(() => openDetail(detail.user_id)); }} data-testid="detail-suspend">Suspend</Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditUser({ user_id: detail.user_id, name: detail.name || "", role: detail.role })} data-testid="detail-edit"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
                  {isAdmin && <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => { if (window.confirm(`Permanently delete ${detail.name}?`)) { act(() => adminApi.adminDeleteUser(detail.user_id), "Account deleted"); setDetail(null); } }} data-testid="detail-delete">Delete</Button>}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog open={!!dtype} onOpenChange={(o) => !o && setDtype(null)}>
        <DialogContent className="max-w-lg" data-testid="dtype-dialog">
          <DialogHeader><DialogTitle>Change driver type</DialogTitle><DialogDescription>{dtype?.name ? `${dtype.name} — ` : ""}re-derives the driver's vehicle, services and permissions, and notifies them.</DialogDescription></DialogHeader>
          {dtype?.loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : dtype ? (
            <div className="space-y-3">
              <div className="space-y-2">
                {VEHICLE_CATEGORIES.map((c) => {
                  const active = dtypeCat === c.value;
                  return (
                    <button key={c.value} onClick={() => setDtypeCat(c.value)} className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${active ? "border-indigo-500 bg-indigo-50" : "border-surface-300 bg-white hover:border-indigo-200"}`} data-testid={`dtype-opt-${c.value}`}>
                      <div className="flex-1">
                        <p className={`text-sm font-semibold ${active ? "text-indigo-700" : "text-ink"}`}>{c.label}</p>
                        <p className="text-xs text-ink-soft">{c.role}</p>
                      </div>
                      {active && <Check className="h-4 w-4 text-indigo-600" />}
                    </button>
                  );
                })}
              </div>
              {INTERCITY_CATS.includes(dtypeCat) && (
                <div className="rounded-xl border border-surface-300 p-3" data-testid="dtype-scope">
                  <p className="mb-2 text-xs font-semibold text-ink">Service area</p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setDtypeScope("local")} className={`flex-1 rounded-full ${dtypeScope === "local" ? "bg-indigo-600 text-white hover:bg-indigo-700" : "border border-surface-300 bg-white text-ink hover:bg-surface-100"}`} data-testid="dtype-scope-local">Local</Button>
                    <Button size="sm" onClick={() => setDtypeScope("regional")} className={`flex-1 rounded-full ${dtypeScope === "regional" ? "bg-indigo-600 text-white hover:bg-indigo-700" : "border border-surface-300 bg-white text-ink hover:bg-surface-100"}`} data-testid="dtype-scope-regional">Regional</Button>
                    <Button size="sm" onClick={() => setDtypeScope("both")} className={`flex-1 rounded-full ${dtypeScope === "both" ? "bg-indigo-600 text-white hover:bg-indigo-700" : "border border-surface-300 bg-white text-ink hover:bg-surface-100"}`} data-testid="dtype-scope-both">Both</Button>
                  </div>
                  <p className="mt-1.5 text-[11px] text-ink-muted">Local = within-city only · Regional = intercity only · Both = either</p>
                </div>
              )}
              <Button onClick={saveDtype} disabled={dtypeBusy || !dtypeChanged} className="w-full rounded-full bg-indigo-600 text-white hover:bg-indigo-700" data-testid="dtype-save">{dtypeBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save driver type</Button>
              {dtype.driver_type_log?.length > 0 && (
                <div className="border-t border-surface-200 pt-2" data-testid="dtype-log">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-indigo-700">Change history</p>
                  <ul className="mt-1 space-y-1">
                    {[...dtype.driver_type_log].reverse().slice(0, 5).map((e, i) => (
                      <li key={i} className="text-[11px] text-ink-soft" data-testid="dtype-log-entry"><span className="font-semibold text-ink">{e.from_label || "—"} → {e.to_label}</span>{e.to_scope ? ` (${e.to_scope})` : ""} · by {e.by_name || "admin"} · {formatDate(e.at)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="max-w-md" data-testid="user-edit-dialog">
          <DialogHeader><DialogTitle>Edit user</DialogTitle><DialogDescription>Update the display name or role. Contact details (email, phone) stay user-verified and can't be changed here.</DialogDescription></DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-ink-soft">Display name</label>
                <Input value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} data-testid="user-edit-name" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-ink-soft">Role</label>
                <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                  <SelectTrigger data-testid="user-edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rider">Customer</SelectItem>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="restaurant">Restaurant</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-ink-muted">Changing the role resets their verification status; drivers/restaurants will need to complete KYC again.</p>
              </div>
              <Button onClick={saveEdit} disabled={editBusy} className="w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="user-edit-save">{editBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
